import { BodyText, HeadingText } from '@calendly/ui/components/typography';
import { colorTextStandard } from '@calendly/ui/theme';
import classNames from 'classnames';
import { forwardRef } from 'react';
import { useMeetingWarning } from 'components/meetings/hooks';
import { MeetingWarningIndicator } from 'components/meetings/MeetingWarningIndicator';
import { Actions } from './actions';
import { HeaderBadge } from './header_badge';
import { MoreActions } from './more_actions';
const panelHeaderContainerCss = "ps6lsh1";
const PanelHeaderContainer = forwardRef((props, ref) => {
  const {
    as: AsComponent,
    ...restProps
  } = props;
  const Component = AsComponent || 'div';
  return <Component ref={ref} {...restProps} className={classNames(panelHeaderContainerCss, props.className)} />;
});
const detailsContainerCss = "d1arre0j";
const DetailsContainer = forwardRef((props, ref) => {
  const {
    as: AsComponent,
    ...restProps
  } = props;
  const Component = AsComponent || 'div';
  return <Component ref={ref} {...restProps} className={classNames(detailsContainerCss, props.className)} />;
});
const containerCss = "c123c8ys";
const Container = forwardRef((props, ref) => {
  const {
    as: AsComponent,
    ...restProps
  } = props;
  const Component = AsComponent || 'div';
  return <Component ref={ref} {...restProps} className={classNames(containerCss, props.className)} />;
});
const headerContainerCss = "h3469q9";
const HeaderContainer = forwardRef((props, ref) => {
  const {
    as: AsComponent,
    ...restProps
  } = props;
  const Component = AsComponent || 'div';
  return <Component ref={ref} {...restProps} className={classNames(headerContainerCss, props.className)} />;
});

/**
 * Pure presentational component for meeting detail header.
 * This component accepts all data as props without any hooks or context dependencies,
 * making it easy to test in isolation and use in Storybook.
 *
 * @param {Object} props
 * @param {Object} props.event - The meeting event object
 * @param {Array} props.todayMeetings - Array of meetings for the same day
 * @param {Object} props.meetingStatus - Meeting status object from useMeetingStatusForDetail
 * @param {Function} props.showReportModal - Handler for showing report modal
 * @param {Function} props.clearSelectedEvent - Handler for clearing the selected event
 * @param {Function} props.updateSelectedEvent - Handler for updating the selected event
 * @param {boolean} props.inProgress - Whether the meeting is currently in progress
 * @param {boolean} props.isFutureMeeting - Whether the meeting is in the future
 * @param {boolean} props.isNotPretending - Whether the user is not pretending
 * @param {Object} props.startTime - Moment object for start time
 * @param {Object} props.now - Current time moment object
 * @param {string} props.timezone - User's timezone
 * @param {string} props.dateNotation - User's date notation preference
 * @param {string} props.timeNotation - User's time notation preference
 * @param {string} props.formattedDate - Pre-formatted date string (e.g., "Monday, January 15, 2024")
 * @param {string} props.formattedTimeRange - Pre-formatted time range (e.g., "10:00am - 11:00am")
 * @param {string} props.eventTypeTimeRange - Pre-formatted event type time range (if different timezone)
 * @param {string} props.eventTypeTimezoneAbbr - Event type timezone abbreviation (e.g., "PST")
 * @param {string} props.expiresAtText - Text for pending event expiration
 * @param {React.Component} props.NotetakerProvider - Provider component for notetaker
 * @param {React.Component} props.StatusIndicator - Optional status indicator component
 */
export const MeetingDetailHeaderView = ({
  event,
  todayMeetings,
  meetingStatus,
  showReportModal,
  clearSelectedEvent,
  updateSelectedEvent,
  inProgress,
  isFutureMeeting,
  isNotPretending,
  startTime,
  now,
  timezone,
  formattedDate,
  formattedTimeRange,
  eventTypeTimeRange,
  eventTypeTimezoneAbbr,
  expiresAtText,
  NotetakerProvider,
  StatusIndicator
}) => {
  const isPending = event.pendingId;
  const isTimezoneDifferent = event.showEventTypeTime;
  const {
    hasRealtimeFeatures,
    warningKey
  } = useMeetingWarning(event, todayMeetings);
  const content = <Container>
      <PanelHeaderContainer>
        <HeaderBadge event={event} todayMeetings={todayMeetings} meetingStatus={meetingStatus} StatusIndicator={StatusIndicator} />
        <DetailsContainer>
          <HeaderContainer>
            <HeadingText size="small" color={colorTextStandard}>{event.name}</HeadingText>
            <MoreActions event={event} showReportModal={showReportModal} clearSelectedEvent={clearSelectedEvent} updateSelectedEvent={updateSelectedEvent} inProgress={inProgress} />
          </HeaderContainer>
          {isPending && expiresAtText && <BodyText size="medium" color="muted">
              {expiresAtText}
            </BodyText>}
          {!isPending && <>
              <BodyText size="medium" color="muted">
                {formattedDate}
              </BodyText>
              <BodyText size="medium" color="muted">
                {formattedTimeRange}
              </BodyText>
              {isTimezoneDifferent && eventTypeTimeRange && <BodyText size="medium" color="muted">
                  {eventTypeTimeRange}
                  {eventTypeTimezoneAbbr && ` (${eventTypeTimezoneAbbr})`}
                </BodyText>}
            </>}
        </DetailsContainer>
        {hasRealtimeFeatures && warningKey && <MeetingWarningIndicator warningKey={warningKey} />}
      </PanelHeaderContainer>
      <Actions event={event} showReportModal={showReportModal} clearSelectedEvent={clearSelectedEvent} updateSelectedEvent={updateSelectedEvent} startTime={startTime} inProgress={inProgress} timezone={timezone} now={now} isFutureMeeting={isFutureMeeting} isNotPretending={isNotPretending} isPending={event.pendingId} isPast={event.past} isCancelled={event.cancelled} />
    </Container>;

  // Wrap in NotetakerProvider if provided
  if (NotetakerProvider) {
    return <NotetakerProvider event={event}>
        {content}
      </NotetakerProvider>;
  }
  return content;
};

require("./meeting_detail_header_view.wyw-in-js.css!=!../../../../../../node_modules/@wyw-in-js/webpack-loader/lib/outputCssLoader.js?cacheProvider=!./meeting_detail_header_view.js");